/* Agents Control Center — merged view of Organogram + AgentSettings.
 *
 * Hierarchy view (mini-org chart at top) + control cards (one per agent)
 * grouped by Master / Department, with idle/running toggle, autonomy pill
 * selector, KPI counts, and click-to-open CV+Train+Tune modal.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { AGENTS, AUTONOMY_LABELS, DEPARTMENT_LABELS, agentById } from "@/lib/agents";
import { openChat } from "@/lib/chatBus";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "./Dashboard";
import { toast } from "sonner";
import i18n from "i18next";
import { ActivityLogModal, AgentLogPanel } from "@/components/AgentActivityLog";
import ScaliqAgentsSection from "@/components/agents/ScaliqAgentsSection";
import {
    X, Crown, CheckCircle2, Activity, RefreshCw, GraduationCap, Sparkles,
    MapPin, Languages, Briefcase, Plus, Trash2, ArrowRight, Settings2,
    Power, Loader2, Pause, PlayCircle, GripVertical, Users, Eye, EyeOff,
    Mail, Linkedin, ScrollText,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

// Stock-photo fallback mapping (original randomuser.me photos, per agent_id)
const STOCK_PHOTOS = {
    master_sales_agent: "https://randomuser.me/api/portraits/men/85.jpg",
    master_recruiter_agent: "https://randomuser.me/api/portraits/women/68.jpg",
    signal_scout_agent: "https://randomuser.me/api/portraits/men/45.jpg",
    sales_outreach_agent: "https://randomuser.me/api/portraits/women/22.jpg",
    sales_qualification_agent: "https://randomuser.me/api/portraits/women/65.jpg",
    content_strategist_agent: "https://randomuser.me/api/portraits/men/55.jpg",
    sourcing_agent: "https://randomuser.me/api/portraits/women/44.jpg",
    candidate_engagement_agent: "https://randomuser.me/api/portraits/men/15.jpg",
    screening_agent: "https://randomuser.me/api/portraits/women/76.jpg",
    notetaker_agent: "https://randomuser.me/api/portraits/men/87.jpg",
    matching_agent: "https://randomuser.me/api/portraits/women/12.jpg",
};

function photoUrlFor(agent, style) {
    if (style === "stock" && STOCK_PHOTOS[agent.id]) return STOCK_PHOTOS[agent.id];
    return agent.photo_url; // default illustrated from agent_defs / agents.js
}

function mergeWithConfigs(configs) {
    return AGENTS.map((base) => {
        const cfg = configs.find((c) => c.agent_id === base.id) || {};
        return {
            ...base,
            ...cfg,
            id: base.id,
            agent_id: base.id,
            autonomy: cfg.autonomy ?? 2,
            is_running: cfg.is_running ?? true,
            llm_provider: cfg.llm_provider || "anthropic",
            llm_model: cfg.llm_model || "claude-sonnet-4-6",
            system_prompt: cfg.system_prompt || "",
            description: cfg.description || "",
            human_name: cfg.human_name || base.human_name,
            title: cfg.title || base.title,
            photo_url: cfg.photo_url || base.photo_url,
            bio: cfg.bio || "",
            expertise: cfg.expertise || [],
            working_style: cfg.working_style || "",
            experience_years: cfg.experience_years || 0,
            languages: cfg.languages || ["nl", "en"],
            training_examples: cfg.training_examples || [],
            // Org-chart structure is canonical from agents.js (NOT overridable by persisted cfg)
            parent: base.parent,
            department: base.department,
            kind: base.kind,
        };
    });
}

function runStatsByAgent(runs) {
    // Aggregate runs from /agents/runs into {agent_id: {today, credits}}
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const out = {};
    for (const r of runs) {
        const t = new Date(r.created_at);
        if (t < today) continue;
        if (!out[r.agent_id]) out[r.agent_id] = { runs_today: 0, credits_today: 0 };
        out[r.agent_id].runs_today += 1;
        out[r.agent_id].credits_today += r.cost_credits || 0;
    }
    return out;
}

/* ------------------------------------------------------------------ */
/*  Main page                                                          */
/* ------------------------------------------------------------------ */
export default function Agents() {
    const { user, workspace } = useAuth();
    const [configs, setConfigs] = useState([]);
    const [runs, setRuns] = useState([]);
    const [selected, setSelected] = useState(null);
    const [showLog, setShowLog] = useState(false);
    const [busy, setBusy] = useState({});
    const [demoFeed, setDemoFeed] = useState([]); // synthetic runs when demo_mode is on
    const [members, setMembers] = useState([]);
    const [invites, setInvites] = useState([]);
    const [hideSubs, setHideSubs] = useState(() => {
        try { return localStorage.getItem("jr_orchestration_hide_subs") === "1"; }
        catch (_) { return false; }
    });

    const photoStyle = workspace?.photo_style || "illustrated";

    const refresh = async () => {
        const [a, b, c, d] = await Promise.all([
            api.get("/agents/configs"),
            api.get("/agents/runs"),
            api.get("/team/members").catch(() => ({ data: [] })),
            api.get("/team/invites").catch(() => ({ data: [] })),
        ]);
        setConfigs(a.data); setRuns(b.data);
        setMembers(c.data || []); setInvites(d.data || []);
    };
    useEffect(() => {
        refresh();
        const t = setInterval(refresh, 10000);
        return () => clearInterval(t);
    }, []);

    const toggleHideSubs = () => {
        const next = !hideSubs;
        setHideSubs(next);
        try { localStorage.setItem("jr_orchestration_hide_subs", next ? "1" : "0"); } catch (_) { /* noop */ }
    };

    // ---- Live demo mode: synthetic activity bursts every 5–10s ----
    useEffect(() => {
        if (!workspace?.demo_mode) { setDemoFeed([]); return; }
        const ids = AGENTS.map((a) => a.id);
        const PHRASES = [
            i18n.t("orch.ph1"),
            i18n.t("orch.ph2"),
            i18n.t("orch.ph3"),
            i18n.t("orch.ph4"),
            i18n.t("orch.ph5"),
            i18n.t("orch.ph6"),
            i18n.t("orch.ph7"),
            i18n.t("orch.ph8"),
            i18n.t("orch.ph9"),
            i18n.t("orch.ph10"),
        ];
        const tick = () => {
            const agent_id = ids[Math.floor(Math.random() * ids.length)];
            const summary = PHRASES[Math.floor(Math.random() * PHRASES.length)];
            setDemoFeed((f) => [{
                id: `demo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
                agent_id, summary, cost_credits: Math.floor(Math.random() * 5),
                created_at: new Date().toISOString(), is_demo: true,
            }, ...f].slice(0, 40));
        };
        const handle = setInterval(tick, 4000 + Math.random() * 4000);
        tick();
        return () => clearInterval(handle);
    }, [workspace?.demo_mode]);

    const allAgents = useMemo(() => {
        const m = mergeWithConfigs(configs);
        // Override photo_url based on workspace.photo_style
        return m.map((a) => ({ ...a, photo_url: photoUrlFor(a, photoStyle) }));
    }, [configs, photoStyle]);

    const combinedRuns = useMemo(() => {
        // Merge demo runs in front, then real runs, dedup by id, cap 60
        return [...demoFeed, ...runs].slice(0, 60);
    }, [demoFeed, runs]);

    const stats = useMemo(() => runStatsByAgent(combinedRuns), [combinedRuns]);
    const byId = Object.fromEntries(allAgents.map((a) => [a.id, a]));

    const activeIds = new Set(
        combinedRuns.filter((r) => Date.now() - new Date(r.created_at).getTime() < 60_000)
            .map((r) => r.agent_id)
    );

    const masterSales = byId.master_sales_agent;
    const masterRec = byId.master_recruiter_agent;
    const masterBridge = byId.matching_agent;
    const salesSubsRaw = allAgents.filter((a) => a.parent === "master_sales_agent");
    const recSubsRaw = allAgents.filter((a) => a.parent === "master_recruiter_agent");
    const bridgeSubsRaw = allAgents.filter((a) => a.parent === "matching_agent");
    const salesSubs = [...salesSubsRaw].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const recSubs = [...recSubsRaw].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const bridgeSubs = [...bridgeSubsRaw].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    const patch = async (agent_id, changes) => {
        setBusy((b) => ({ ...b, [agent_id]: true }));
        try {
            await api.patch(`/agents/configs/${agent_id}`, changes);
            // optimistic update
            setConfigs((cs) => cs.map((c) =>
                c.agent_id === agent_id ? { ...c, ...changes } : c
            ));
        } catch (e) {
            toast.error(e?.response?.data?.detail || i18n.t("orch.update_failed"));
        } finally {
            setBusy((b) => { const n = { ...b }; delete n[agent_id]; return n; });
        }
    };

    const canMutate = user?.role === "owner" || user?.role === "admin";

    const reorderAgents = async (_parentId, ordered_ids) => {
        // optimistic local update
        setConfigs((cs) => {
            const orderMap = Object.fromEntries(ordered_ids.map((id, i) => [id, i]));
            return cs.map((c) => orderMap[c.agent_id] !== undefined ? { ...c, order: orderMap[c.agent_id] } : c);
        });
        try {
            await api.patch("/workspace-settings/agents/reorder", { agent_ids: ordered_ids });
        } catch (e) { toast.error(i18n.t("orch.order_failed")); refresh(); }
    };

    return (
        <PageHeader
            title="Agents"
            subtitle="12 specialisten · georkestreerd door 3 masters · jij beslist."
            actions={
                <div className="flex items-center gap-2">
                    {workspace?.demo_mode && (
                        <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] px-2.5 py-1 rounded-full bg-[var(--jr-info)]/15 text-[var(--jr-info)] border border-[var(--jr-info)]/30"
                            data-testid="demo-mode-badge">
                            <Sparkles size={10} /> Demo modus
                        </span>
                    )}
                    <button onClick={toggleHideSubs} data-testid="toggle-hide-subs"
                        className={`inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-mono uppercase tracking-[0.16em] border transition-colors ${hideSubs
                            ? "bg-[var(--jr-info)]/10 text-[var(--jr-info)] border-[var(--jr-info)]/40"
                            : "border-white/10 text-[var(--jr-text-2)] hover:text-white hover:bg-white/5"}`}>
                        {hideSubs ? <EyeOff size={12} /> : <Eye size={12} />}
                        {hideSubs ? "Sub-agents verborgen" : "Toon sub-agents"}
                    </button>
                    <button onClick={() => setShowLog(true)} data-testid="open-activity-log"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-mono uppercase tracking-[0.16em] text-[var(--jr-text-2)] hover:text-white border border-white/10 hover:bg-white/5">
                        <ScrollText size={12} /> Logboek
                    </button>
                    <button onClick={refresh} data-testid="agents-refresh"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-mono uppercase tracking-[0.16em] text-[var(--jr-text-2)] hover:text-white border border-white/10 hover:bg-white/5">
                        <RefreshCw size={12} /> Refresh
                    </button>
                </div>
            }
        >
            {/* Top: humans / orchestrators */}
            <HumansBanner user={user} members={members || []} invites={invites || []} />

            {/* Department: Sales (Vincent + 4 subs) */}
            <Section
                title="Sales · Vincent Peeters"
                lead={masterSales}
                subs={salesSubs}
                color="var(--jr-brand)"
                stats={stats}
                activeIds={activeIds}
                busy={busy}
                canMutate={canMutate}
                onSelect={setSelected}
                onReorder={reorderAgents}
                onPatch={patch}
                hideSubs={hideSubs}
            />

            {/* Department: Recruitment (Hannah + 3 subs) */}
            <Section
                title="Recruitment · Hannah Claes"
                lead={masterRec}
                subs={recSubs}
                color="var(--jr-amber)"
                stats={stats}
                activeIds={activeIds}
                busy={busy}
                canMutate={canMutate}
                onSelect={setSelected}
                onReorder={reorderAgents}
                onPatch={patch}
                hideSubs={hideSubs}
            />

            {/* Department: Bridge — Matching & Onboarding (Emma + 2 subs) */}
            <Section
                title="Matching & Onboarding · Emma Hofman"
                lead={masterBridge}
                subs={bridgeSubs}
                color="var(--jr-info)"
                stats={stats}
                activeIds={activeIds}
                busy={busy}
                canMutate={canMutate}
                onSelect={setSelected}
                onReorder={reorderAgents}
                onPatch={patch}
                hideSubs={hideSubs}
            />

            {/* LinkedIn-agents via ScalIQ (accounts + auto-push & reply-agent) */}
            <ScaliqAgentsSection canMutate={canMutate} />

            {/* Live activity ticker (incl synthetic demo bursts when demo_mode on) */}
            <LiveActivity runs={combinedRuns} byId={byId} demoMode={workspace?.demo_mode} />

            {showLog && <ActivityLogModal onClose={() => setShowLog(false)} />}

            {selected && (
                <AgentModal
                    agent={selected}
                    canMutate={canMutate}
                    onClose={() => setSelected(null)}
                    onSaved={refresh}
                />
            )}
        </PageHeader>
    );
}

/* ------------------------------------------------------------------ */
/*  Humans banner — orchestrators sit ABOVE all agent masters          */
/* ------------------------------------------------------------------ */
function HumansBanner({ user, members, invites }) {
    const navigate = (path) => () => { window.location.href = path; };
    const others = (members || []).filter((m) => m.id !== user?.id);
    return (
        <div className="mb-6 rounded-xl border border-white/8 bg-[var(--jr-surface)] p-5 relative overflow-hidden"
            data-testid="humans-banner">
            <div aria-hidden className="absolute inset-0 bg-dotgrid opacity-20 pointer-events-none" />
            <div aria-hidden className="absolute -top-20 left-1/2 -translate-x-1/2 w-[360px] h-[360px] rounded-full bg-[var(--jr-brand)]/6 blur-[100px] pointer-events-none" />
            <div className="relative">
                <div className="flex items-center justify-between mb-3">
                    <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--jr-text-3)]">Dirigenten · jij + team</div>
                    <button onClick={navigate("/app/team")} data-testid="humans-team-link"
                        className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-2)] hover:text-white inline-flex items-center gap-1">
                        Team beheren <ArrowRight size={10} />
                    </button>
                </div>
                <div data-testid="humans-row" className="flex items-center gap-2 flex-wrap">
                    <button onClick={navigate("/app/team")}
                        title={`${user?.name} · ${user?.role}`}
                        data-testid={`human-self-${user?.id}`}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[var(--jr-brand)] text-black text-[10px] font-mono uppercase tracking-[0.18em] hover:opacity-90">
                        <Crown size={11} /> {user?.name?.split(" ")[0]} · Jij
                    </button>
                    {others.map((m) => (
                        <button key={m.id} onClick={navigate("/app/team")}
                            title={`${m.name} · ${m.role}${m.linkedin_url ? " · " + m.linkedin_url : ""}`}
                            data-testid={`human-${m.id}`}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/15 bg-[var(--jr-surface-2)] hover:bg-white/[0.06] text-[10px] font-mono uppercase tracking-[0.18em] text-white">
                            <Users size={11} className="text-[var(--jr-text-2)]" />
                            {m.name?.split(" ")[0] || m.email?.split("@")[0]}
                            <span className="text-[var(--jr-text-3)] normal-case tracking-normal">· {m.role}</span>
                            {m.linkedin_url && <Linkedin size={9} className="text-[#0a66c2]" />}
                        </button>
                    ))}
                    {(invites || []).map((inv) => (
                        <span key={inv.id} title={`Uitgenodigd: ${inv.email}`}
                            data-testid={`invite-${inv.id}`}
                            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-dashed border-white/15 text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] opacity-70">
                            <Mail size={10} /> {inv.name || inv.email.split("@")[0]} · pending
                        </span>
                    ))}
                </div>
                <div aria-hidden className="mt-4 flex justify-center">
                    <div className="w-px h-6 bg-gradient-to-b from-white/30 via-white/10 to-white/0" />
                </div>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Workload heatmap — color the avatar ring based on today's load     */
/* ------------------------------------------------------------------ */
function workloadColor(runs, credits) {
    // Combine signal — runs weight high (real activity), credits second
    const score = Math.min(100, Math.max(runs * 8, credits / 2));
    if (score >= 71) return { ring: "rgba(239,68,68,0.85)", glow: "rgba(239,68,68,0.45)", label: "Heavy" };
    if (score >= 31) return { ring: "rgba(245,158,11,0.75)", glow: "rgba(245,158,11,0.35)", label: "Busy" };
    if (score >= 1)  return { ring: "rgba(34,197,94,0.65)",  glow: "rgba(34,197,94,0.30)",  label: "Light" };
    return { ring: "rgba(148,163,184,0.28)", glow: "transparent", label: "Idle" };
}

/* ------------------------------------------------------------------ */
/*  Department section (lead card + sub cards) — bulk pause + drag&drop */
/* ------------------------------------------------------------------ */
function Section({ title, lead, subs, color, stats, activeIds, busy, canMutate, onSelect, onPatch, onReorder, hideSubs }) {
    const [dragId, setDragId] = useState(null);
    const [overId, setOverId] = useState(null);
    if (!lead) return null;

    const effSubs = hideSubs ? [] : subs;
    const subRunningCount = subs.filter((s) => s.is_running).length;
    const allRunning = subs.length > 0 && subRunningCount === subs.length;
    const allPaused = subs.length > 0 && subRunningCount === 0;

    const onDragStart = (id) => (e) => {
        if (!canMutate) return;
        setDragId(id);
        e.dataTransfer.effectAllowed = "move";
    };
    const onDragOver = (id) => (e) => {
        if (!canMutate || !dragId || dragId === id) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setOverId(id);
    };
    const onDrop = (id) => (e) => {
        e.preventDefault();
        if (!canMutate || !dragId || dragId === id) { setDragId(null); setOverId(null); return; }
        const ids = subs.map((s) => s.id);
        const from = ids.indexOf(dragId);
        const to = ids.indexOf(id);
        if (from === -1 || to === -1) { setDragId(null); setOverId(null); return; }
        const next = [...ids];
        next.splice(from, 1);
        next.splice(to, 0, dragId);
        onReorder?.(lead.parent ? null : lead.id, next);
        setDragId(null); setOverId(null);
    };

    return (
        <div className="mb-10" data-testid={`section-${lead.id}`}>
            <div className="mb-3 flex items-center gap-2 flex-wrap">
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
                <h2 className="font-display text-xl tracking-tight">{title}</h2>
                <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">
                    {subs.length > 0 ? `1 lead${hideSubs ? "" : ` + ${subs.length} subagents`}` : "1 specialist"}
                </span>
                {canMutate && !hideSubs && subs.length > 0 && (
                    <div className="ml-auto flex items-center gap-1.5">
                        <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] hidden sm:inline">
                            {subRunningCount}/{subs.length} running
                        </span>
                        <button onClick={() => onPatch && subs.forEach((s) => onPatch(s.id, { is_running: false }))}
                            disabled={allPaused}
                            data-testid={`bulk-pause-${lead.id}`}
                            className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.16em] px-2.5 py-1 rounded-full border border-white/10 bg-white/[0.02] text-[var(--jr-text-2)] hover:bg-white/[0.06] hover:text-white disabled:opacity-40">
                            <Pause size={10} /> Pauzeer
                        </button>
                        <button onClick={() => onPatch && subs.forEach((s) => onPatch(s.id, { is_running: true }))}
                            disabled={allRunning}
                            data-testid={`bulk-resume-${lead.id}`}
                            className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.16em] px-2.5 py-1 rounded-full border border-[#22c55e]/30 bg-[#22c55e]/[0.08] text-[#22c55e] hover:bg-[#22c55e]/[0.15] disabled:opacity-40">
                            <PlayCircle size={10} /> Resume
                        </button>
                    </div>
                )}
            </div>

            {/* Master card — centered, half-width on desktop */}
            <div className="flex justify-center" data-testid={`section-master-${lead.id}`}>
                <div className="w-full lg:w-1/2 xl:w-2/5">
                    <AgentCard agent={lead} isMaster color={color}
                        stats={stats[lead.id]} active={activeIds.has(lead.id)} busy={busy[lead.id]}
                        canMutate={canMutate} onSelect={onSelect} onPatch={onPatch} />
                </div>
            </div>

            {/* Org-chart connectors + sub row */}
            {!hideSubs && effSubs.length > 0 && (
                <div className="relative" data-testid={`section-subs-${lead.id}`}>
                    {/* Vertical trunk down from master */}
                    <div aria-hidden className="flex justify-center">
                        <div className="w-px h-6" style={{ background: `linear-gradient(to bottom, ${color}AA 0%, ${color}55 100%)` }} />
                    </div>
                    {/* Horizontal trunk (only when more than 1 sub) */}
                    {effSubs.length > 1 && (
                        <OrgChartTrunk count={effSubs.length} color={color} />
                    )}
                    {/* Sub cards row */}
                    <div className={`grid gap-3 mt-2 ${effSubs.length === 1 ? "grid-cols-1 max-w-md mx-auto" :
                        effSubs.length === 2 ? "grid-cols-1 sm:grid-cols-2 max-w-3xl mx-auto" :
                            effSubs.length === 3 ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" :
                                "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
                        }`}>
                        {effSubs.map((s) => (
                            <div key={s.id}
                                draggable={canMutate}
                                onDragStart={onDragStart(s.id)}
                                onDragOver={onDragOver(s.id)}
                                onDrop={onDrop(s.id)}
                                onDragEnd={() => { setDragId(null); setOverId(null); }}
                                data-testid={`drag-wrap-${s.id}`}
                                className={`relative transition-all ${dragId === s.id ? "opacity-50" : ""} ${overId === s.id ? "scale-[1.01]" : ""}`}>
                                {/* Drop-line from horizontal trunk into card */}
                                <span aria-hidden className="absolute left-1/2 -top-2 w-px h-2 -translate-x-1/2"
                                    style={{ background: color + "AA" }} />
                                {/* Small junction dot */}
                                <span aria-hidden className="absolute left-1/2 -top-[7px] w-1.5 h-1.5 rounded-full -translate-x-1/2 ring-2 ring-[var(--jr-bg)]"
                                    style={{ background: color }} />
                                {overId === s.id && (
                                    <div className="absolute -inset-1 rounded-2xl border-2 border-dashed border-[var(--jr-brand)]/50 pointer-events-none" />
                                )}
                                <AgentCard agent={s} color={color}
                                    stats={stats[s.id]} active={activeIds.has(s.id)} busy={busy[s.id]}
                                    canMutate={canMutate} onSelect={onSelect} onPatch={onPatch} draggable />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

/* Horizontal trunk that spans the sub row + drops a tick over each col */
function OrgChartTrunk({ count, color }) {
    // The horizontal line spans from first sub center to last sub center
    // Inside a grid with N columns, centers are at (k+0.5)/N of the row width
    const firstPct = (0.5 / count) * 100;
    const lastPct = ((count - 0.5) / count) * 100;
    return (
        <div aria-hidden className="relative h-2">
            <div className="absolute top-0 hidden sm:block h-px"
                style={{ left: `${firstPct}%`, right: `${100 - lastPct}%`, background: color + "AA" }} />
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Agent control card (Agent Control Center style)                    */
/* ------------------------------------------------------------------ */
function AgentCard({ agent, isMaster, color, stats, active, busy, canMutate, onSelect, onPatch, draggable }) {
    const dep = DEPARTMENT_LABELS[agent.department] || DEPARTMENT_LABELS.sales;
    const lvl = AUTONOMY_LABELS[agent.autonomy] || AUTONOMY_LABELS[2];
    const runs = stats?.runs_today ?? 0;
    const credits = stats?.credits_today ?? 0;

    const toggleRunning = (e) => {
        e.stopPropagation();
        if (!canMutate) return;
        onPatch(agent.id, { is_running: !agent.is_running });
    };
    const setAutonomy = (e, lv) => {
        e.stopPropagation();
        if (!canMutate) return;
        onPatch(agent.id, { autonomy: lv });
    };

    return (
        <div
            data-testid={`agent-card-${agent.id}`}
            onClick={() => onSelect(agent)}
            className={`group relative cursor-pointer rounded-2xl p-5 transition-all hover:translate-y-[-1px] hover:bg-white/[0.02] flex flex-col gap-4 ${
                isMaster ? "border-2" : "border border-white/10 bg-[var(--jr-surface)]"
            }`}
            style={isMaster ? { borderColor: color + "55", background: `linear-gradient(180deg, ${color}0c 0%, transparent 60%), var(--jr-surface)` } : {}}
        >
            {/* Drag handle indicator (only for sub-cards) */}
            {draggable && canMutate && (
                <span aria-hidden title="Sleep om te herschikken"
                    className="absolute top-2 left-2 text-[var(--jr-text-3)] opacity-0 group-hover:opacity-60 transition-opacity cursor-grab active:cursor-grabbing">
                    <GripVertical size={12} />
                </span>
            )}
            {/* Top row: avatar + name | running toggle */}
            <div className="flex items-start gap-3">
                <div className="relative shrink-0" data-testid={`avatar-wrap-${agent.id}`}>
                    {(() => {
                        const wl = workloadColor(runs, credits);
                        return (
                            <>
                                <span aria-hidden
                                    title={i18n.t("orch.workload_tip", { label: wl.label, runs, credits })}
                                    className="absolute -inset-1 rounded-full transition-all"
                                    style={{ boxShadow: `0 0 0 2px ${wl.ring}, 0 0 16px -2px ${wl.glow}` }} />
                                <img src={agent.photo_url} alt={agent.human_name}
                                    data-testid={`avatar-img-${agent.id}`}
                                    className="relative w-14 h-14 rounded-full object-cover bg-white/5" />
                                <span aria-hidden
                                    data-testid={`workload-tag-${agent.id}`}
                                    className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-[8px] font-mono uppercase tracking-[0.16em] px-1.5 py-[1px] rounded-full ring-2 ring-[var(--jr-surface)]"
                                    style={{ background: wl.ring, color: "#0a0a0c", fontWeight: 700 }}>
                                    {wl.label}
                                </span>
                            </>
                        );
                    })()}
                    {active && (
                        <span className="absolute -top-0.5 -right-0.5 w-3 h-3 rounded-full bg-[var(--jr-brand)] jr-pulse ring-2 ring-[var(--jr-surface)] z-10" />
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                        <h3 className="font-display text-lg tracking-tight text-white truncate">{agent.human_name}</h3>
                        {isMaster && <Crown size={12} style={{ color }} className="shrink-0" />}
                    </div>
                    <div className="text-xs text-[var(--jr-text-2)] truncate">{agent.title}</div>
                </div>
                {/* Running / Idle toggle */}
                <RunningToggle running={agent.is_running} busy={busy} disabled={!canMutate} onToggle={toggleRunning} />
            </div>

            {/* Department badge with checkmark */}
            <div className="flex flex-wrap items-center gap-1.5">
                <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded-full"
                    style={{ background: dep.color + "1F", color: dep.color }}
                    data-testid={`dept-badge-${agent.id}`}>
                    <CheckCircle2 size={10} /> {dep.label}
                </span>
                <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-[0.18em] px-2 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[var(--jr-text-3)]">
                    {agent.kind === "master" ? "Master" : "Sub"}
                </span>
                <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] ml-auto truncate">
                    {agent.name}
                </span>
            </div>

            {/* Description */}
            <p className="text-xs text-[var(--jr-text-2)] leading-relaxed line-clamp-3 min-h-[3em]">
                {agent.description || agent.bio || i18n.t("orch.no_description")}
            </p>

            {/* Autonomy pill selector */}
            <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)] mb-1.5">Autonomy</div>
                <div className="grid grid-cols-3 gap-1 rounded-full bg-white/[0.03] p-1" role="radiogroup">
                    {[1, 2, 3].map((lv) => {
                        const lvLabel = AUTONOMY_LABELS[lv];
                        const isActive = agent.autonomy === lv;
                        const lvColor = lv === 3 ? "var(--jr-brand)" : lv === 2 ? "var(--jr-amber)" : "var(--jr-info)";
                        return (
                            <button key={lv}
                                onClick={(e) => setAutonomy(e, lv)}
                                disabled={!canMutate}
                                role="radio" aria-checked={isActive}
                                data-testid={`autonomy-${agent.id}-${lv}`}
                                className={`text-[10px] font-mono uppercase tracking-[0.14em] py-1.5 rounded-full transition-all ${
                                    isActive ? "text-black font-bold" : "text-[var(--jr-text-2)] hover:text-white"
                                }`}
                                style={isActive ? { background: lvColor, boxShadow: `0 4px 12px -4px ${lvColor}80` } : {}}>
                                {lvLabel.short}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* KPI footer */}
            <div className="pt-3 border-t border-white/8 flex items-center justify-between text-[11px]">
                <span className="text-[var(--jr-text-3)]">
                    <span className="text-white font-medium">{runs}</span> runs vandaag
                </span>
                <span className="text-[var(--jr-text-3)]">
                    <span className="font-medium" style={{ color: credits > 0 ? "var(--jr-amber)" : "var(--jr-text-2)" }}>{credits}</span> credits
                </span>
            </div>
        </div>
    );
}

function RunningToggle({ running, busy, disabled, onToggle }) {
    return (
        <button type="button" onClick={onToggle} disabled={disabled}
            aria-pressed={!!running}
            aria-label={running ? "Pauzeer agent" : "Start agent"}
            data-testid="running-toggle"
            className="shrink-0 inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.16em] px-2 py-1 rounded-full transition-colors border"
            style={running
                ? { background: "rgba(34,197,94,0.12)", borderColor: "rgba(34,197,94,0.35)", color: "#22c55e" }
                : { background: "var(--jr-surface-2)", borderColor: "rgba(255,255,255,0.10)", color: "var(--jr-text-3)" }}>
            {busy ? <Loader2 size={10} className="animate-spin" /> : (
                running
                    ? <><span className="w-1.5 h-1.5 rounded-full bg-[#22c55e] jr-pulse" /> Running</>
                    : <><Power size={10} /> Idle</>
            )}
        </button>
    );
}

/* ------------------------------------------------------------------ */
/*  Live activity ticker                                               */
/* ------------------------------------------------------------------ */
function LiveActivity({ runs, byId, demoMode }) {
    return (
        <div className="mt-2 rounded-xl border border-white/8 bg-[var(--jr-surface)] p-5" data-testid="live-activity">
            <div className="flex items-center gap-2 mb-3">
                <Activity size={14} className="text-[var(--jr-brand)]" />
                <h3 className="font-display text-base tracking-tight">Live activiteit</h3>
                {demoMode && (
                    <span className="text-[10px] font-mono uppercase tracking-[0.16em] px-2 py-0.5 rounded-full bg-[var(--jr-info)]/15 text-[var(--jr-info)]">Demo</span>
                )}
                <span className="text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)] ml-auto">{runs.length} recent</span>
            </div>
            {runs.length === 0 ? (
                <div className="text-xs text-[var(--jr-text-3)] italic">{i18n.t("orch.no_runs_today")}</div>
            ) : (
                <ul className="space-y-1.5 max-h-[220px] overflow-y-auto">
                    {runs.slice(0, 12).map((r) => {
                        const ag = byId[r.agent_id];
                        return (
                            <li key={r.id} className="flex items-center gap-3 text-xs border-l-2 pl-3 py-1"
                                style={{ borderColor: r.is_demo ? "var(--jr-info)" : "rgba(255,255,255,0.10)" }}
                                data-testid={`run-${r.id}`}>
                                {ag?.photo_url ? (
                                    <img src={ag.photo_url} alt="" className="w-6 h-6 rounded-full object-cover bg-white/5 border border-white/10" />
                                ) : <span className="w-6 h-6 rounded-full bg-white/5" />}
                                <span className="text-white min-w-[140px] truncate">{ag?.human_name || r.agent_id}</span>
                                <span className="flex-1 text-[var(--jr-text-2)] truncate">{r.summary}</span>
                                {r.is_demo && <span className="text-[9px] text-[var(--jr-info)] font-mono uppercase tracking-[0.12em]">DEMO</span>}
                                <span className="text-[10px] font-mono text-[var(--jr-text-3)]">{r.cost_credits || 0}c</span>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Agent detail modal — CV / Train / Tune tabs                        */
/* ------------------------------------------------------------------ */
function AgentModal({ agent, canMutate, onClose, onSaved }) {
    const [tab, setTab] = useState("cv");
    const [examples, setExamples] = useState(agent.training_examples || []);
    const [draft, setDraft] = useState({ title: "", instruction: "", example_input: "", example_output: "" });
    const [saving, setSaving] = useState(false);

    const dep = DEPARTMENT_LABELS[agent.department] || DEPARTMENT_LABELS.sales;
    const close = (e) => { if (e.target === e.currentTarget) onClose(); };

    const addExample = async (e) => {
        e.preventDefault();
        if (!draft.title.trim() || !draft.instruction.trim()) {
            toast.error("Geef titel en instructie op.");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post(`/agents/configs/${agent.id}/train`, draft);
            setExamples((x) => [...x, data]);
            setDraft({ title: "", instruction: "", example_input: "", example_output: "" });
            toast.success(i18n.t("orch.trained_ok", { name: agent.human_name?.split(" ")[0] }));
            onSaved?.();
        } catch (err) {
            toast.error(err?.response?.data?.detail || i18n.t("orch.train_failed"));
        } finally { setSaving(false); }
    };

    const removeExample = async (id) => {
        try {
            await api.delete(`/agents/configs/${agent.id}/train/${id}`);
            setExamples((x) => x.filter((e) => e.id !== id));
            onSaved?.();
        } catch (e) { /* noop */ }
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={close}>
            <div className="w-full max-w-4xl max-h-[90vh] bg-[var(--jr-surface)] border border-white/10 rounded-2xl jr-fadein flex flex-col overflow-hidden"
                data-testid="agent-cv-modal" onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="relative p-6 border-b border-white/8 shrink-0">
                    <div aria-hidden className="absolute inset-0 pointer-events-none"
                        style={{ background: `linear-gradient(90deg, ${dep.color}10 0%, transparent 60%)` }} />
                    <button onClick={onClose} aria-label="Sluit" data-testid="agent-cv-close"
                        className="absolute top-4 right-4 z-10 text-[var(--jr-text-2)] hover:text-white">
                        <X size={20} />
                    </button>
                    <div className="relative flex items-start gap-5">
                        <img src={agent.photo_url} alt={agent.human_name}
                            className="w-24 h-24 rounded-2xl object-cover bg-white/5 border border-white/10 shrink-0" />
                        <div className="flex-1 min-w-0">
                            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--jr-text-3)]">{agent.name}</div>
                            <h2 className="font-display text-3xl tracking-tight text-white mt-1">{agent.human_name}</h2>
                            <div className="text-sm text-[var(--jr-text-2)] mt-1">{agent.title}</div>
                            <div className="flex flex-wrap gap-2 mt-3">
                                <Pill icon={Briefcase}>{agent.experience_years} jaar</Pill>
                                <Pill icon={MapPin} color={dep.color}>
                                    <CheckCircle2 size={10} /> {dep.label}
                                </Pill>
                                <Pill icon={Languages}>{(agent.languages || []).join(" · ").toUpperCase()}</Pill>
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-1 mt-5 relative">
                        <TabBtn active={tab === "cv"} onClick={() => setTab("cv")} testid="agent-tab-cv">CV</TabBtn>
                        <TabBtn active={tab === "train"} onClick={() => setTab("train")} testid="agent-tab-train">
                            <GraduationCap size={12} /> Train ({examples.length})
                        </TabBtn>
                        <TabBtn active={tab === "log"} onClick={() => setTab("log")} testid="agent-tab-log">
                            <ScrollText size={12} /> Logboek
                        </TabBtn>
                        {canMutate && (
                            <TabBtn active={tab === "tune"} onClick={() => setTab("tune")} testid="agent-tab-tune">
                                <Settings2 size={12} /> Tune
                            </TabBtn>
                        )}
                    </div>
                </div>

                {/* Body */}
                <div className="p-6 flex-1 overflow-y-auto">
                    {tab === "cv" && <CVTab agent={agent} />}
                    {tab === "log" && <AgentLogPanel agentId={agent.id} />}
                    {tab === "train" && (
                        <TrainTab agent={agent} examples={examples} draft={draft} setDraft={setDraft}
                            saving={saving} onAdd={addExample} onRemove={removeExample} canMutate={canMutate} />
                    )}
                    {tab === "tune" && canMutate && <TuneTab agent={agent} onSaved={onSaved} />}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-white/8 bg-[var(--jr-surface-2)]/40 flex items-center justify-between text-xs shrink-0">
                    <span className="font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">Agent ID · {agent.id}</span>
                    <button type="button" onClick={() => openChat({ masterId: agent.parent || agent.id })}
                        className="inline-flex items-center gap-1.5 text-[var(--jr-brand)] hover:text-[var(--jr-brand-2)]"
                        data-testid="agent-cv-chat">
                        Praat met {(agent.parent ? agentById(agent.parent)?.human_name : agent.human_name)?.split(" ")[0]}
                        <ArrowRight size={12} />
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ----- modal tabs ----- */
function CVTab({ agent }) {
    return (
        <div className="space-y-6">
            <Block title="Bio">
                <p className="text-sm text-[var(--jr-text-2)] leading-relaxed">{agent.bio}</p>
            </Block>
            <Block title="Expertise">
                <div className="flex flex-wrap gap-2">
                    {(agent.expertise || []).map((s) => (
                        <span key={s} className="text-xs px-3 py-1 rounded-full border border-white/10 bg-white/[0.03] text-[var(--jr-text-2)]">{s}</span>
                    ))}
                </div>
            </Block>
            <Block title="Werkstijl">
                <p className="text-sm text-[var(--jr-text-2)] leading-relaxed italic">&ldquo;{agent.working_style}&rdquo;</p>
            </Block>
            <Block title={i18n.t("orch.what_does")}>
                <p className="text-sm text-[var(--jr-text-2)] leading-relaxed">{agent.description}</p>
            </Block>
        </div>
    );
}

function TrainTab({ agent, examples, draft, setDraft, saving, onAdd, onRemove, canMutate }) {
    return (
        <div className="space-y-5">
            {canMutate && (
                <div className="rounded-xl border border-white/10 bg-[var(--jr-surface-2)] p-4">
                    <div className="flex items-center gap-2 mb-3">
                        <Sparkles size={14} className="text-[var(--jr-brand)]" />
                        <h4 className="text-sm font-medium text-white">{i18n.t("orch.give_instruction", { name: agent.human_name?.split(" ")[0] })}</h4>
                    </div>
                    <form onSubmit={onAdd} className="space-y-3">
                        <TextField label={i18n.t("search.lbl_title")} required value={draft.title}
                            onChange={(v) => setDraft((d) => ({ ...d, title: v }))}
                            placeholder={i18n.t("orch.train_title_ph")} testid="train-title" />
                        <TextArea label={i18n.t("orch.lbl_instruction")} required value={draft.instruction} rows={3}
                            onChange={(v) => setDraft((d) => ({ ...d, instruction: v }))}
                            placeholder={i18n.t("orch.train_instr_ph")}
                            testid="train-instruction" />
                        <div className="grid sm:grid-cols-2 gap-3">
                            <TextArea label={i18n.t("orch.lbl_ex_input")} value={draft.example_input} rows={3}
                                onChange={(v) => setDraft((d) => ({ ...d, example_input: v }))}
                                placeholder={i18n.t("orch.ex_input_ph")} testid="train-input" />
                            <TextArea label={i18n.t("orch.lbl_ex_output")} value={draft.example_output} rows={3}
                                onChange={(v) => setDraft((d) => ({ ...d, example_output: v }))}
                                placeholder={i18n.t("orch.ex_output_ph")} testid="train-output" />
                        </div>
                        <div className="flex justify-end">
                            <button type="submit" disabled={saving} data-testid="train-submit"
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--jr-brand)] text-white text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-60">
                                <Plus size={14} /> {saving ? i18n.t("orch.training") : i18n.t("orch.add_training")}
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--jr-text-3)] mb-2">
                    {i18n.t("orch.already_learned")} ({examples.length})
                </div>
                {examples.length === 0 ? (
                    <div className="text-sm text-[var(--jr-text-3)] italic">{i18n.t("orch.no_examples")}</div>
                ) : (
                    <ul className="space-y-2">
                        {examples.map((ex) => (
                            <li key={ex.id} className="rounded-lg border border-white/8 bg-[var(--jr-surface-2)] p-3 flex items-start justify-between gap-3"
                                data-testid={`train-example-${ex.id}`}>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm text-white font-medium">{ex.title}</div>
                                    <div className="text-xs text-[var(--jr-text-2)] mt-1">{ex.instruction}</div>
                                </div>
                                {canMutate && (
                                    <button onClick={() => onRemove(ex.id)} aria-label={i18n.t("common.delete")}
                                        className="text-[var(--jr-text-3)] hover:text-[var(--jr-danger)] shrink-0">
                                        <Trash2 size={14} />
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

function TuneTab({ agent, onSaved }) {
    const [provider, setProvider] = useState(agent.llm_provider);
    const [model, setModel] = useState(agent.llm_model);
    const [prompt, setPrompt] = useState(agent.system_prompt);
    const [saving, setSaving] = useState(false);

    const MODELS = {
        anthropic: ["claude-sonnet-4-6", "claude-haiku-4-5"],
        openai: ["gpt-5.4", "gpt-5.4-mini", "gpt-5.2"],
        gemini: ["gemini-3.1-pro-preview", "gemini-3-flash"],
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.patch(`/agents/configs/${agent.id}`, {
                llm_provider: provider, llm_model: model, system_prompt: prompt,
            });
            toast.success(i18n.t("orch.tuning_saved"));
            onSaved?.();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally { setSaving(false); }
    };

    return (
        <form onSubmit={save} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
                <div>
                    <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">LLM provider</label>
                    <select value={provider} data-testid="tune-provider"
                        onChange={(e) => { setProvider(e.target.value); setModel(MODELS[e.target.value][0]); }}
                        className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
                        <option value="anthropic">Anthropic Claude</option>
                        <option value="openai">OpenAI GPT</option>
                        <option value="gemini">Google Gemini</option>
                    </select>
                </div>
                <div>
                    <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">Model</label>
                    <select value={model} data-testid="tune-model"
                        onChange={(e) => setModel(e.target.value)}
                        className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white">
                        {(MODELS[provider] || []).map((m) => <option key={m} value={m}>{m}</option>)}
                    </select>
                </div>
            </div>
            <div>
                <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">System prompt</label>
                <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={10}
                    data-testid="tune-prompt"
                    className="mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none font-mono" />
                <p className="mt-1 text-[10px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">
                    Tip: begin met &quot;Je bent {agent.human_name}…&quot;. Houd het beknopt en specifiek.
                </p>
            </div>
            <div className="flex justify-end">
                <button type="submit" disabled={saving} data-testid="tune-save"
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[var(--jr-brand)] text-white text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-60">
                    {saving ? <><Loader2 className="animate-spin" size={14} /> {i18n.t("common.saving")}</> : i18n.t("common.save")}
                </button>
            </div>
        </form>
    );
}

/* ----- tiny UI helpers ----- */
function Block({ title, children }) {
    return (
        <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--jr-text-3)] mb-2">{title}</div>
            {children}
        </div>
    );
}
function Pill({ icon: Icon, color, children }) {
    return (
        <span className="inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border font-mono uppercase tracking-[0.14em]"
            style={color
                ? { background: color + "1F", color, borderColor: color + "33" }
                : { background: "rgba(255,255,255,0.03)", color: "var(--jr-text-2)", borderColor: "rgba(255,255,255,0.10)" }}>
            {Icon && <Icon size={11} />} {children}
        </span>
    );
}
function TabBtn({ active, onClick, children, testid }) {
    return (
        <button type="button" onClick={onClick} data-testid={testid}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-mono uppercase tracking-[0.16em] transition-colors ${
                active ? "bg-white/10 text-white" : "text-[var(--jr-text-2)] hover:bg-white/5"
            }`}>
            {children}
        </button>
    );
}
function TextField({ label, value, onChange, placeholder, required, testid }) {
    return (
        <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{label}{required && " *"}</label>
            <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
                className="mt-1 w-full bg-[var(--jr-surface)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none" />
        </div>
    );
}
function TextArea({ label, value, onChange, placeholder, rows = 3, required, testid }) {
    return (
        <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{label}{required && " *"}</label>
            <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows} data-testid={testid}
                className="mt-1 w-full bg-[var(--jr-surface)] border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:border-[var(--jr-brand)] outline-none" />
        </div>
    );
}
